<template>
  <RevCardCarousel
    v-if="hasArticles"
    :id="textToTarget(title, 'heading-2')"
    :class="{ [$style.carouselCardOrder]: double && articles.length > 3 }"
    :next-alternative-text="i18n(translations.nextSlideText)"
    :prev-alternative-text="i18n(translations.prevSlideText)"
    :subtitle
    :title
  >
    <ArticleCard
      v-for="(article, index) in articles"
      :key="article.id"
      data-test="article-card"
      v-bind="article"
      :tracking="trackingData({ index })"
    />

    <template v-if="cta" #button>
      <RevButton
        :icon="IconArrowRight"
        :rel="cta?.link?.rel"
        size="medium"
        :target="cta?.link?.target"
        :to="cta?.link?.href"
        variant="primary"
        @click="sendCTATracking"
      >
        {{ cta?.label }}
      </RevButton>
    </template>
  </RevCardCarousel>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { CTALink } from '@backmarket/http-api/src/api-specs-content/models/cta-link'
import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevCardCarousel } from '@ds/components/CardCarousel'
import { IconArrowRight } from '@ds/icons/IconArrowRight'

import { textToTarget } from '../../utils/textToTarget'

import type { ArticleCard as ArticleCardType } from './ArticleCard.types'
import ArticleCard from './ArticleCard.vue'
import translations from './ArticleCardsCarousel.translations'

const props = withDefaults(
  defineProps<{
    articles: ArticleCardType[]
    tracking?: Tracking
    title?: string
    subtitle?: string
    cta?: CTALink
    double?: boolean
  }>(),
  {
    tracking: () => ({}),
    title: undefined,
    subtitle: undefined,
    cta: undefined,
    double: false,
  },
)

const i18n = useI18n()
const { trackClick } = useTracking()

const hasArticles = computed(() => props.articles.length > 0)

const trackingData = ({ index }: { index: number }) => ({
  ...props.tracking,
  position: index + 1,
  ...(props.title && { blockTitle: props.title }),
})

function sendCTATracking() {
  if (isEmpty(props.tracking)) return

  trackClick({
    ...props.tracking,
    value: `${props.tracking.value}_CTA`,
  })
}
</script>

<style module>
.carouselCardOrder > div > ul {
  @apply gap-16 grid-rows-2 grid-flow-col;
}
</style>
